import React, { useMemo, useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService';
import { User, Vacancy } from '../types';
import { 
  Briefcase, Users, Calendar, Clock, 
  ArrowRight, CheckCircle2, AlertCircle, Play 
} from 'lucide-react';
import TodoWidget from './TodoWidget';

interface ClientDashboardProps {
  user: User;
  vacancies: Vacancy[];
  candidates: any[];
}

const ClientDashboard: React.FC<ClientDashboardProps> = ({ user, vacancies, candidates }) => {
  const [realCandidateCount, setRealCandidateCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchScopedCandidateCount = async () => {
      try {
        if (!user.clientId && vacancies.length === 0) return;
        
        // Use applications table mapped to their vacancies to find total active candidates
        const vacIds = vacancies.map(v => v.id);
        if (vacIds.length > 0) {
           let totalUniqueCandidates = new Set();
           // Chunk to avoid url too long
           for (let i = 0; i < vacIds.length; i += 100) {
               const chunk = vacIds.slice(i, i + 100);
               const { data } = await supabase.from('applications').select('candidate_id, status').in('vacancy_id', chunk);
               if (data) {
                   data.forEach(a => { 
                       const s = (a.status || 'New').toLowerCase();
                       if (s !== 'shortlist' && s !== 'new' && s !== 'nieuw' && s !== 'screening' && s !== 'op shortlist') {
                           if (a.candidate_id) totalUniqueCandidates.add(a.candidate_id); 
                       }
                   });
               }
           }
           setRealCandidateCount(totalUniqueCandidates.size);
        } else {
           setRealCandidateCount(0);
        }
      } catch (e) { console.error('Count error', e); }
    };
    fetchScopedCandidateCount();
  }, [vacancies, user.clientId]);

  // --- METRICS ---
  const metrics = useMemo(() => {
    const activeVacanciesCount = vacancies.filter(v => (v.status as string) !== 'Closed' && (v.status as string) !== 'Concept').length;
    
    // Recent Updates (last 5 candidates)
    const recentCandidates = [...candidates]
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
      .slice(0, 5);

    // Agenda (Candidates in Interview or upcoming Todos - mocking via Candidate Stage for now)
    const upcomingInterviews = candidates
      .filter(c => c.status === 'Interview' || c.stage === 'Interview')
      .slice(0, 3);

    // Active pipeline count (Not hired, not closed, not rejected)
    const inPipeline = candidates.filter(c => c.status !== 'Placed' && c.status !== 'Hired' && c.status !== 'Rejected').length;

    return {
      activeVacanciesCount: activeVacanciesCount,
      totalCandidates: realCandidateCount !== null ? realCandidateCount : candidates.length,
      inPipelineCount: inPipeline,
      recentCandidates,
      upcomingInterviews
    };
  }, [vacancies, candidates, realCandidateCount]);

  const safeFormatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'Recent';
    try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return 'Recent';
        // If today
        if (d.toDateString() === new Date().toDateString()) return 'Vandaag';
        return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
    } catch {
        return 'Recent';
    }
  };

  return (
    <div className="p-4 lg:p-8 w-full mx-auto space-y-6 animate-fadeIn pb-24">
      
      {/* Header & High-Level Stats */}
      <div className="bg-[#0F172A] p-6 rounded-[2rem] shadow-xl relative overflow-hidden text-white">
        <div className="absolute right-0 top-0 w-64 h-64 bg-[#e51b23] rounded-full blur-[80px] opacity-20 -mr-20 -mt-20 pointer-events-none"></div>
        
        <div className="relative z-10 mb-6">
          <h1 className="text-xl sm:text-2xl font-black tracking-tight break-words">Welkom terug, {user.name.split(' ')[0]}</h1>
          <p className="text-xs font-bold text-slate-400 mt-1">Hier is je persoonlijke actiecentrum.</p>
        </div>

        <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/10 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2 text-slate-300">
                 <Briefcase size={16} />
                 <h3 className="text-[10px] font-black uppercase tracking-widest">Open Vacatures</h3>
              </div>
              <p className="text-3xl font-black">{metrics.activeVacanciesCount}</p>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/10 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2 text-slate-300">
                 <Users size={16} />
                 <h3 className="text-[10px] font-black uppercase tracking-widest">Actieve Kandidaten</h3>
              </div>
              <p className="text-3xl font-black">{metrics.totalCandidates}</p>
            </div>
            {realCandidateCount === null && <p className="text-[9px] text-slate-400 mt-1">Laden...</p>}
          </div>
          <div className="bg-indigo-500/20 backdrop-blur-md p-4 rounded-2xl border border-indigo-400/30 flex flex-col justify-between cursor-pointer hover:bg-indigo-500/30 transition-all group" onClick={() => window.dispatchEvent(new CustomEvent('navigate-tab', { detail: 'pipeline' }))}>
             <div>
                <div className="flex items-center justify-between text-indigo-300 mb-2">
                   <div className="flex items-center gap-2">
                      <Play size={16} />
                      <h3 className="text-[10px] font-black uppercase tracking-widest text-indigo-300">Active Pipeline & Shortlist</h3>
                   </div>
                   <ArrowRight size={14} className="opacity-50 group-hover:translate-x-1 transition-transform" />
                </div>
                <p className="text-xl font-bold text-indigo-100 mt-2">Bekijk het Kanban board</p>
             </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          
          {/* TO-DO LIST (Left Column on Desktop) */}
          <div className="flex flex-col h-[500px]">
             <TodoWidget 
                userId={user.id} 
                clientId={user.clientId} 
                isOpen={true} 
                onToggle={() => {}} 
                inline={true} 
             />
          </div>

          {/* Right Column (Updates & Agenda) */}
          <div className="space-y-6">
             
             {/* AGENDA */}
             <div className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                   <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg"><Calendar size={18} /></div>
                   <h2 className="text-lg font-black text-slate-900 tracking-tight">Agenda & Interviews</h2>
                </div>
                
                {metrics.upcomingInterviews.length > 0 ? (
                   <div className="space-y-3">
                      {metrics.upcomingInterviews.map((c, i) => (
                         <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-100 rounded-xl">
                            <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex flex-col items-center justify-center border border-slate-200 shrink-0">
                               <span className="text-[9px] font-bold text-slate-400 uppercase leading-none">Binnen</span>
                               <span className="text-xs font-black text-slate-900">Kort</span>
                            </div>
                            <div className="flex-1 min-w-0">
                               <p className="text-sm font-bold text-slate-900 truncate">{c.candidate?.name || c.name || 'Kandidaat'}</p>
                               <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest">Interview Gepland</p>
                            </div>
                            <button className="p-2 text-slate-400 hover:text-indigo-600 bg-white shadow-sm border border-slate-100 rounded-lg">
                               <Play size={14} />
                            </button>
                         </div>
                      ))}
                   </div>
                ) : (
                   <div className="text-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                      <Calendar size={24} className="mx-auto text-slate-300 mb-2" />
                      <p className="text-xs font-bold text-slate-500">Geen interviews gepland deze week.</p>
                   </div>
                )}
             </div>

             {/* RECENT UPDATES */}
             <div className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                   <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg"><Clock size={18} /></div>
                   <h2 className="text-lg font-black text-slate-900 tracking-tight">Recente Updates</h2>
                </div>
                
                {metrics.recentCandidates.length > 0 ? (
                   <div className="space-y-3 relative before:absolute before:inset-y-0 before:left-5 before:w-px before:bg-slate-100">
                       {metrics.recentCandidates.map((c, i) => (
                          <div 
                             key={i} 
                             className="relative flex items-start gap-4 z-10 group cursor-pointer"
                             onClick={() => {
                                // Dispatch event to open candidate or navigate to talentpool
                                window.dispatchEvent(new CustomEvent('open-candidate', { detail: c.candidate_id || c.id }));
                                window.dispatchEvent(new CustomEvent('navigate-tab', { detail: 'talentpool' }));
                             }}
                          >
                             <div className="w-10 h-10 bg-white rounded-full border-4 border-white shadow-sm flex items-center justify-center text-xs font-black text-slate-600 shrink-0 z-10 group-hover:border-indigo-100 group-hover:text-indigo-600 transition-colors">
                                {c.candidate?.name?.charAt(0) || c.name?.charAt(0) || '?'}
                             </div>
                             <div className="flex-1 min-w-0 bg-slate-50 p-3 rounded-xl border border-slate-100 group-hover:border-indigo-200 group-hover:shadow-sm transition-all group-hover:bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <div>
                                   <div className="flex justify-between items-start mb-1">
                                      <p className="text-xs font-bold text-slate-900 truncate pr-2 group-hover:text-indigo-700">
                                         {c.candidate?.name || c.name || 'Nieuwe Kandidaat'}
                                      </p>
                                      <span className="text-[9px] font-bold text-slate-400 shrink-0 sm:hidden">
                                         {safeFormatDate(c.created_at)}
                                      </span>
                                   </div>
                                   <p className="text-[10px] font-medium text-slate-500 line-clamp-2">
                                      Nieuwe sollicitatie ontvangen voor {c.vacancy_title || 'een vacature'}. 
                                      Status is nu <strong className="text-slate-700">{c.status || c.stage || 'Nieuw'}</strong>.
                                   </p>
                                </div>
                                <div className="flex items-center gap-2 mt-2 sm:mt-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                   <span className="text-[9px] font-bold text-slate-400 shrink-0 hidden sm:block">
                                      {safeFormatDate(c.created_at)}
                                   </span>
                                   <button className="text-[10px] font-black uppercase tracking-widest text-[#e51b23] hover:text-red-700 mt-1 sm:mt-0 shrink-0 flex items-center gap-1">
                                       Zie meer <ArrowRight size={12} />
                                   </button>
                                </div>
                             </div>
                          </div>
                       ))}
                    </div>
                 ) : (
                   <div className="text-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                      <CheckCircle2 size={24} className="mx-auto text-slate-300 mb-2" />
                      <p className="text-xs font-bold text-slate-500">Helemaal up-to-date!</p>
                   </div>
                )}
             </div>

          </div>
      </div>

    </div>
  );
};

export default ClientDashboard;
